import { useQuery } from "@tanstack/react-query";

import { useCurrentOrgId } from "@/providers/auth/hooks";
import { useDomainServices } from "@/providers/DomainServices";

import { ingestionRulesQueryKeys } from "./ingestionRulesQueryKeys";

/** Every ingestion rule in the org. */
export function useIngestionRules(orgId?: string) {
  const { ingestionRuleService } = useDomainServices();
  const resolvedOrgId = useCurrentOrgId(orgId);

  return useQuery({
    queryKey: ingestionRulesQueryKeys.list(resolvedOrgId),
    queryFn: ({ signal }) =>
      ingestionRuleService.listRules(resolvedOrgId, { abortSignal: signal }),
    staleTime: 30_000,
  });
}

/** The rules Roboto offers every org, and whether the org has each already. */
export function useDefaultIngestionRules(orgId?: string) {
  const { ingestionRuleService } = useDomainServices();
  const resolvedOrgId = useCurrentOrgId(orgId);

  return useQuery({
    queryKey: ingestionRulesQueryKeys.defaults(resolvedOrgId),
    queryFn: ({ signal }) =>
      ingestionRuleService.listDefaultRules(resolvedOrgId, {
        abortSignal: signal,
      }),
    staleTime: 30_000,
  });
}

/** One ingestion rule by id; idle while `ruleId` is undefined. */
export function useIngestionRule(ruleId: string | undefined, orgId?: string) {
  const { ingestionRuleService } = useDomainServices();
  const resolvedOrgId = useCurrentOrgId(orgId);

  return useQuery({
    queryKey: ingestionRulesQueryKeys.byId(resolvedOrgId, ruleId ?? ""),
    queryFn: ({ signal }) =>
      ingestionRuleService.getRule(ruleId ?? "", resolvedOrgId, {
        abortSignal: signal,
      }),
    enabled: ruleId !== undefined,
  });
}

/**
 * The org's rules that match one file path, as the server's matcher decides.
 * Idle until `enabled`, so a list of files asks only about the one someone
 * opens.
 */
export function useIngestionRulesMatching(
  path: string,
  { enabled, orgId }: { enabled: boolean; orgId?: string },
) {
  const { ingestionRuleService } = useDomainServices();
  const resolvedOrgId = useCurrentOrgId(orgId);

  return useQuery({
    queryKey: ingestionRulesQueryKeys.matching(resolvedOrgId, path),
    queryFn: async ({ signal }) => {
      const matches = await ingestionRuleService.matchRules(
        [path],
        resolvedOrgId,
        { abortSignal: signal },
      );
      return matches[path] ?? [];
    },
    enabled,
    staleTime: 30_000,
  });
}

/**
 * The org's rules that match any of `paths` (at most 200), each rule once, as
 * the server's matcher decides. Idle while `paths` is empty.
 */
export function useIngestionRulesMatchingAny(
  paths: readonly string[],
  { enabled = true, orgId }: { enabled?: boolean; orgId?: string } = {},
) {
  const { ingestionRuleService } = useDomainServices();
  const resolvedOrgId = useCurrentOrgId(orgId);

  return useQuery({
    queryKey: ingestionRulesQueryKeys.matchingAny(resolvedOrgId, paths),
    queryFn: async ({ signal }) => {
      const matches = await ingestionRuleService.matchRules(
        paths,
        resolvedOrgId,
        { abortSignal: signal },
      );
      const byId = new Map(
        Object.values(matches)
          .flat()
          .map((rule) => [rule.rule_id, rule]),
      );
      return [...byId.values()];
    },
    enabled: enabled && paths.length > 0,
    staleTime: 30_000,
  });
}
